import React from 'react';
import {useTranslation} from 'react-i18next';
import {SharedBarChart} from '../../charts/SharedBarChart';
import {SharedPieChart} from '../../charts/SharedPieChart';
import {DataExplorerLineChart} from '../../data_explorer/DataExplorerLineChart';
import {DataExplorerTable} from '../../data_explorer/DataExplorerTable';
import {FilterNotesList} from '../../elements/FilterNotesDisplay';
import {filterErrorText} from '../../../utils/filterNotes';
import {fieldLabel} from '../state/fieldLabels';
import {barsChartData, groupsByFirstColumn, hidesEmptyRows, kpiView, lineChart, pieHasSlices, showsHeatmap, tableResults} from './panelData';

/**
 * Los cuerpos de los cinco tipos de panel. Cada uno recibe el `data` del motor para SU panel y
 * envuelve el widget del Explorador correspondiente sin tocarlo (tabla y línea) o el chart
 * compartido (barras, torta). Los estados EMPTY / ERROR se pintan acá, uniformes.
 *
 * `filterNotes` es lo que `state/panelFilterNotes.js` resolvió para el panel (tanda «Filtros que
 * se respetan», 2026-09-28): qué métricas no aplican con los filtros pedidos y qué notas van
 * debajo del valor. **Sin notas es `null` y cada cuerpo dibuja exactamente lo de siempre**
 * (guardián: `__tests__/PanelBodies.sinNotas.test.js`). El motivo del «no aplica» vive en el ⓘ
 * del encabezado (`PanelFilterNotesWarning`), no acá: el cuerpo tiene `overflow: hidden` y un
 * cartel adentro se recortaría.
 */
export function PanelState({state, errorCode, explainedAbove = false}) {
    const {t} = useTranslation();
    if (state === 'NO_METRIC') {
        return <div className="inf-panel-state -empty">{t('informes.panel.no_metric', 'Elige una métrica para esta tarjeta.')}</div>;
    }
    if (state === 'LOADING') {
        return <div className="inf-panel-state -loading">{t('informes.panel.loading', 'Calculando…')}</div>;
    }
    if (state === 'UNAVAILABLE') {
        // Este panel no tiene datos y tampoco puede afirmar que no los haya. Dos palabras y tono
        // neutro a propósito — el motivo y la acción se cuentan UNA vez, en la franja de arriba;
        // doce cajas rojas repitiendo el mismo párrafo son el mismo ruido que se quiere evitar.
        //
        // Dos caminos llegan acá y el hint los distingue: el informe falló ENTERO (no hay aviso
        // arriba que leer), o este panel falló por la causa que el aviso YA explica.
        const hint = explainedAbove
            ? t('informes.panel.unavailable_hint_explained', 'El motivo está en el aviso de arriba.')
            : t('informes.panel.unavailable_hint', 'No se pudo calcular el informe, así que este bloque no tiene datos para mostrar.');
        return (
            <div className="inf-panel-state -muted" title={hint}>
                {t('informes.panel.unavailable', 'No disponible')}
            </div>
        );
    }
    if (state === 'ERROR') {
        // Los errores de filtro de la tanda (`copy.md` §3) tienen su texto en `filter_notes.*`,
        // compartido con el Explorador; el resto se resuelve por código en `informes.panel.error`.
        const filterText = filterErrorText(errorCode, null, t);
        if (filterText) return <div className="inf-panel-state -error">{filterText}</div>;
        // Un código que el FE no conoce cae al texto genérico, nunca al código crudo: el backend
        // tipa más errores de los que hay traducidos y el usuario leía «PERIOD_NOT_SUPPORTED».
        const fallback = t('informes.panel.error.QUERY_ERROR', 'No se pudo calcular este panel.');
        return <div className="inf-panel-state -error">{t(`informes.panel.error.${errorCode || 'QUERY_ERROR'}`, fallback)}</div>;
    }
    return <div className="inf-panel-state -empty">{t('informes.panel.empty', 'Sin datos para este período')}</div>;
}

/**
 * El cuerpo de un gráfico cuya(s) métrica(s) no se puede(n) calcular con los filtros pedidos: un
 * «—» y «No aplica», en el gris de «No disponible». Nunca «Sin datos para este período», que
 * afirma algo sobre el negocio del cliente; ni un 0, que es lo que el nodo dejó de mandar.
 */
export function NotApplicableState({notes = []}) {
    const {t} = useTranslation();
    return (
        <div className="inf-panel-state -muted -not-applicable" data-testid="inf-not-applicable">
            <span className="fn-na"><span className="fn-na-dash" aria-hidden="true">—</span>{t('filter_notes.na_short', 'No aplica')}</span>
            <FilterNotesList texts={notes}/>
        </div>
    );
}

function allNotApplicable(filterNotes) {
    return !!(filterNotes && filterNotes.allNotApplicable);
}

function excludedMetrics(filterNotes) {
    return filterNotes ? filterNotes.notApplicableMetrics : [];
}

function notesOf(filterNotes) {
    return filterNotes ? filterNotes.notes : [];
}

export function KpiBody({data, panel, filterNotes = null}) {
    const {t} = useTranslation();
    const view = kpiView(data);
    // La métrica no aplica con los filtros: el valor es «—» (el motivo está en el ⓘ del
    // encabezado) y las notas, debajo. Va antes que el vacío porque el motor puede haber dejado
    // la tarjeta sin `data`.
    if (allNotApplicable(filterNotes)) {
        return (
            <div className="inf-kpi -neutral -not-applicable" data-testid="inf-kpi-not-applicable">
                <div className="inf-kpi-value fn-na">
                    <span className="fn-na-dash" aria-hidden="true">—</span>
                    {/* El lector de pantalla no lee un guion: el «No aplica» va oculto a la vista. */}
                    <span className="visually-hidden">{t('filter_notes.na_short', 'No aplica')}</span>
                </div>
                <FilterNotesList texts={notesOf(filterNotes)}/>
            </div>
        );
    }
    // El «Elige una métrica para esta tarjeta» se decide ARRIBA, en `panelRenderState`: desde que
    // el cuerpo se dibuja sólo con estado `OK`, a este `return` no se llegaba nunca —hacía falta
    // `data == null`, que ya no renderiza cuerpo— y la tarjeta vacía terminaba diciendo «Sin datos
    // para este período» (review R-reports-fe-11). La decisión vive en un solo lugar.
    if (!view) return <PanelState state="EMPTY"/>;
    return (
        <div className={`inf-kpi -${view.sentiment}`}>
            <div className="inf-kpi-value">{view.value}</div>
            {view.delta && (
                // `-dir-up` y no `-up`: `_tables.scss` tiene una regla GLOBAL `.-up:after` /
                // `.-down:after` que pega una flecha FontAwesome a cualquier elemento con esas
                // clases, y la pill mostraba dos flechas (QA caso 04, «flecha duplicada»).
                <div className={`inf-kpi-delta -dir-${view.direction}`}>
                    {view.direction === 'up' ? '↑ ' : (view.direction === 'down' ? '↓ ' : '')}{view.delta}
                </div>
            )}
            <FilterNotesList texts={notesOf(filterNotes)}/>
        </div>
    );
}

export function BarsBody({data, fieldsData = null, filterNotes = null}) {
    if (allNotApplicable(filterNotes)) return <NotApplicableState notes={notesOf(filterNotes)}/>;
    // Las series con el nombre del catálogo («Valor de compra»), no con el id (Hesta, E9).
    const chartData = barsChartData(data, excludedMetrics(filterNotes), (metric) => fieldLabel(metric, fieldsData));
    if (!chartData) return <PanelState state="EMPTY"/>;
    return (
        <>
            <div className="inf-chart" style={{height: 300}}>
                <SharedBarChart chartData={chartData} height={300} showLegend={chartData.series.length > 1}/>
            </div>
            <FilterNotesList texts={notesOf(filterNotes)}/>
        </>
    );
}

export function PieBody({data, filterNotes = null}) {
    const {i18n} = useTranslation();
    // La torta dibuja UNA métrica (`data.metric`): si ésa no aplica, el panel no aplica, aunque el
    // motor haya devuelto otras en `metrics` (review C-fe-05: con `value` en null, `pieHasSlices`
    // decía «Sin datos para este período»).
    const drawnMetric = data && data.metric;
    if (allNotApplicable(filterNotes) || (drawnMetric && excludedMetrics(filterNotes).includes(drawnMetric))) {
        return <NotApplicableState notes={notesOf(filterNotes)}/>;
    }
    // Sin una sola porción > 0 no hay torta que dibujar: el mismo vacío que las barras y la línea.
    if (!pieHasSlices(data)) return <PanelState state="EMPTY"/>;
    return (
        <>
            {/* `-pie` acota el techo de la leyenda a ESTE gráfico: es el único con leyenda vertical al
                costado. Sin la clase, la regla alcanzaba también a la leyenda horizontal de las barras y
                la comprimía al 45%, partiéndola en dos renglones (QA-VIS-07). */}
            <div className="inf-chart -pie" style={{height: 300}}>
                {/* Las etiquetas se recortan y el valor entero queda en el `title`: una dimensión
                    de URLs hacía crecer la leyenda hasta tapar el selector del panel. */}
                <SharedPieChart rows={data.rows} height={300} legendLabelMaxChars={28}
                                showShare locale={i18n?.language || 'es'}/>
            </div>
            <FilterNotesList texts={notesOf(filterNotes)}/>
        </>
    );
}

export function LineBody({data, filterNotes = null}) {
    if (allNotApplicable(filterNotes)) return <NotApplicableState notes={notesOf(filterNotes)}/>;
    const {chartData, selectedMetrics} = lineChart(data, excludedMetrics(filterNotes));
    if (!chartData) return <PanelState state="EMPTY"/>;
    return (
        <>
            <DataExplorerLineChart chartData={chartData} selectedMetrics={selectedMetrics} showTooltip/>
            <FilterNotesList texts={notesOf(filterNotes)}/>
        </>
    );
}

export function TableBody({data, fieldsData, panel = null, filterNotes = null}) {
    // La tabla resuelve las notas por columna, con el mismo helper que el Explorador: «—» en las
    // celdas y el Total, y el motivo en el ⓘ del encabezado de esa columna. Si el motor la dejó
    // sin filas porque NINGUNA de sus métricas aplica, «No aplica» (review C-fe-10).
    const results = tableResults(data, filterNotes ? filterNotes.raw : null, filterNotes ? filterNotes.summaryRaw : undefined);
    if (!results && allNotApplicable(filterNotes)) return <NotApplicableState notes={notesOf(filterNotes)}/>;
    if (!results || !fieldsData) return <PanelState state="EMPTY"/>;
    // «Ocultar filas sin datos» viaja sólo prendido: apagado (el default) la tabla recibe lo de siempre.
    const hideProps = hidesEmptyRows(panel) ? {hideEmptyRows: true} : {};
    // El mapa de calor y el agrupado son del PANEL (se guardan con el informe), no del navegador: la
    // tabla no toca localStorage y el cliente ve en el portal lo que dejó la agencia. `stableOrder`:
    // arranca ordenada por la primera dimensión y desempata por las demás (`tableOrdering.js`).
    return <DataExplorerTable loading={false} results={results} fieldsData={fieldsData} {...hideProps}
                              heatMapEnabled={showsHeatmap(panel)} stableOrder
                              groupByFirstColumn={groupsByFirstColumn(panel)}/>;
}
